<template>
  <div v-if="activeActivities.length" class="lead-activity-icons" :class="{ 'lead-activity-icons--crm': iconSet === 'crm', 'lead-activity-icons--nowrap': nowrap }" aria-label="Lead activities">
    <AppStatusBadge
      v-for="activity in visibleActivities"
      :key="activity.code"
      v-tooltip="iconOnly || labelTooltips ? { content: activity.name, placement: 'top', offset: 8, classes: 'tooltip--new' } : null"
      :design="design"
      :color="activity.color"
      class="lead-activity-icons__item"
      :class="[
        `lead-activity-icons__item--${activity.code}`,
        { 'lead-activity-icons__item--icon-only': iconOnly, 'ui-tooltip-target': keyboardTooltips && (iconOnly || labelTooltips) }
      ]"
      :tabindex="keyboardTooltips && (iconOnly || labelTooltips) ? 0 : null"
      :aria-label="activity.name"
    >
      <img class="lead-activity-icons__glyph" :src="iconSet === 'crm' ? activity.crmAsset : activity.asset" alt="" aria-hidden="true">
      <span v-if="!iconOnly" class="lead-activity-icons__label">{{ activity.name }}</span>
    </AppStatusBadge>
    <AppStatusBadge
      v-if="hiddenActivitiesCount"
      v-tooltip="{ content: hiddenActivitiesLabel, placement: 'top', offset: 8, classes: 'tooltip--new' }"
      :design="design"
      tone="neutral"
      compact
      class="lead-activity-icons__more"
      :class="{ 'ui-tooltip-target': keyboardTooltips }"
      :tabindex="keyboardTooltips ? 0 : null"
      :label="`+${hiddenActivitiesCount}`"
    />
  </div>
</template>

<script>
const ACTIVITY_ALIASES = {
  day_trips: 'day_trip',
  island_vacation: 'island'
}

const ACTIVITY_OPTIONS = [
  {
    code: 'climbing',
    name: 'Climbing',
    color: '#4d7c0f',
    asset: require('@/assets/img/lead-flow/activity-climbing.png'),
    crmAsset: require('@/assets/img/climbing-table-icon.svg')
  },
  {
    code: 'safari',
    name: 'Safari',
    color: '#b85d05',
    asset: require('@/assets/img/lead-flow/activity-safari.png'),
    crmAsset: require('@/assets/img/safari-table-icon.svg')
  },
  {
    code: 'day_trip',
    name: 'Day trips',
    color: '#0369a1',
    asset: require('@/assets/img/lead-flow/activity-day-trip.png'),
    crmAsset: require('@/assets/img/day_trips-table-icon.svg')
  },
  {
    code: 'island',
    name: 'Island vacation',
    color: '#0e7490',
    asset: require('@/assets/img/lead-flow/activity-island.png'),
    crmAsset: require('@/assets/img/island-table-icon.svg')
  }
]

export default {
  name: 'LeadActivityIcons',
  props: {
    nowrap: { type: Boolean, default: false },
    iconSet: { type: String, default: 'flow', validator: value => ['flow', 'crm'].includes(value) },
    keyboardTooltips: { type: Boolean, default: false },
    labelTooltips: { type: Boolean, default: true },
    design: {
      type: String,
      required: true,
      validator: value => value === 'new'
    },
    activities: {
      type: Array,
      default: () => []
    },
    maxVisible: {
      type: Number,
      default: 3
    },
    iconOnly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    activeActivities() {
      const activeCodes = this.activities.map(activity => {
        const code = typeof activity === 'string' ? activity : activity.code
        return ACTIVITY_ALIASES[code] || code
      })
      return ACTIVITY_OPTIONS.filter(activity => activeCodes.includes(activity.code))
    },
    visibleActivities() {
      return this.activeActivities.slice(0, this.maxVisible)
    },
    hiddenActivities() {
      return this.activeActivities.slice(this.maxVisible)
    },
    hiddenActivitiesCount() {
      return this.hiddenActivities.length
    },
    hiddenActivitiesLabel() {
      return this.hiddenActivities.map(activity => activity.name).join(', ')
    }
  }
}
</script>

<style lang="scss" scoped>
.lead-activity-icons {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.lead-activity-icons__item {
  padding-left: 5px;
}

.lead-activity-icons__item--icon-only {
  min-width: 30px;
  flex: 0 0 auto;
  padding: 5px;
}

.lead-activity-icons__glyph {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  filter: brightness(0) invert(1);
  object-fit: contain;
  opacity: .96;
}

.lead-activity-icons__item--safari .lead-activity-icons__glyph {
  transform: scale(1.15);
}

.lead-activity-icons__item--climbing .lead-activity-icons__glyph {
  transform: scale(1.4);
}

.lead-activity-icons__item--day_trip .lead-activity-icons__glyph {
  transform: scale(1.72);
}

.lead-activity-icons__item--island .lead-activity-icons__glyph {
  transform: scale(1.45);
}

.lead-activity-icons--crm .lead-activity-icons__glyph {
  transform: none;
}

.lead-activity-icons--crm {
  gap: $ui-space-4;

  .lead-activity-icons__item--icon-only {
    width: 30px;
    height: 30px;
    flex: 0 0 30px;
    padding: 0;
  }
}

.lead-activity-icons--nowrap {
  flex-wrap: nowrap;
}

.lead-activity-icons__more {
  flex: 0 0 auto;
}

.lead-activity-icons__label {
  overflow: hidden;
  color: inherit;
  font-size: inherit;
  text-overflow: ellipsis;
}
</style>
